import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { AspectRatio } from '../components/ui/aspect-ratio';
import { Carousel, CarouselContent, CarouselItem, CarouselNext, CarouselPrevious } from '../components/ui/carousel';
import { Paintbrush, Home as HomeIcon, Building2, Ruler, ArrowRight } from 'lucide-react';
import BeforeAfter from '../components/BeforeAfter';
import { motion } from 'framer-motion';

export default function Home() {
  const services = [
    {
      icon: <HomeIcon className="h-6 w-6" />,
      title: 'Residential Design',
      description: 'Transform your home into a personalized sanctuary with our bespoke interior solutions.'
    },
    {
      icon: <Building2 className="h-6 w-6" />,
      title: 'Commercial Spaces',
      description: 'Create inspiring work environments that boost productivity and reflect your brand.'
    },
    {
      icon: <Ruler className="h-6 w-6" />,
      title: 'Space Planning',
      description: 'Optimize your layout for maximum functionality and aesthetic appeal.'
    },
    {
      icon: <Paintbrush className="h-6 w-6" />,
      title: 'Custom Furniture',
      description: 'Bespoke furniture pieces designed and crafted to perfectly fit your space.'
    }
  ];

  const featuredImages = [
    'https://images.unsplash.com/photo-1704428381439-5ed443412617',
    'https://images.unsplash.com/photo-1682888813795-192fca4a10d9',
    'https://images.unsplash.com/photo-1617325247661-675ab4b64ae2',
    'https://images.unsplash.com/photo-1704428381312-0579346a779c',
    'https://images.unsplash.com/photo-1714860534425-7ce04e013dec',
    'https://images.unsplash.com/photo-1718717722247-26f4c6c09192',
    'https://images.unsplash.com/photo-1720247520862-7e4b14176fa8',
    'https://images.unsplash.com/photo-1519086588705-c935fdedcc14'
  ];

  const testimonials = [
    {
      quote: "Interio Mart transformed our apartment into a beautiful, functional space. Their attention to detail is impeccable.",
      author: "Priya Sharma",
      location: "Bengaluru"
    },
    {
      quote: "Professional, creative, and efficient. They delivered our office redesign on time and within budget.",
      author: "Rajesh Kumar",
      location: "Mumbai"
    },
    {
      quote: "The team understood our vision perfectly and brought it to life. Highly recommend their services!",
      author: "Anita Desai",
      location: "Bengaluru"
    }
  ];

  return (
    <div data-testid="home-page">
      {/* Hero Section */}
      <section className="hero-bg py-16 md:py-24">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-screen-xl">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              <h2 className="text-base md:text-lg font-semibold tracking-wide uppercase text-muted-foreground mb-4">
                Premium Interior Design
              </h2>
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-tight leading-tight mb-6">
                Crafting Elegant Spaces for Modern Living
              </h1>
              <p className="text-base md:text-lg leading-relaxed text-foreground/90 mb-8">
                Experience the perfect blend of minimalist aesthetics and functional design. We transform your vision into timeless interiors that inspire and delight.
              </p>
              <div className="flex flex-wrap gap-4">
                <Link to="/contact">
                  <Button size="lg" className="transition-colors duration-200" data-testid="hero-cta-button">
                    Start Your Project
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                </Link>
                <Link to="/gallery">
                  <Button variant="secondary" size="lg" className="transition-colors duration-200" data-testid="hero-gallery-button">
                    View Gallery
                  </Button>
                </Link>
              </div>
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.6, delay: 0.2 }}
            >
              <Card className="overflow-hidden shadow-lg">
                <AspectRatio ratio={4/3}>
                  <img
                    src="https://images.unsplash.com/photo-1705321963943-de94bb3f0dd3"
                    alt="Modern minimalist living room"
                    className="w-full h-full object-cover"
                  />
                </AspectRatio>
              </Card>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Services Bento */}
      <section className="py-16 md:py-24">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-screen-xl">
          <div className="text-center mb-12">
            <h2 className="text-base md:text-lg font-semibold tracking-wide uppercase text-muted-foreground mb-4">
              Our Services
            </h2>
            <h3 className="text-3xl md:text-4xl font-semibold">
              Comprehensive Interior Solutions
            </h3>
          </div>
          
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {services.map((service, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4, delay: index * 0.1 }}
                viewport={{ once: true }}
              >
                <Card className="p-6 rounded-2xl border bg-white card-hover" data-testid={`service-card-${index}`}>
                  <div className="text-primary mb-4">{service.icon}</div>
                  <h4 className="text-xl font-semibold mb-2">{service.title}</h4>
                  <p className="text-sm text-muted-foreground leading-relaxed">{service.description}</p>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Featured Gallery */}
      <section className="py-16 md:py-24 bg-muted">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-screen-xl">
          <div className="text-center mb-12">
            <h2 className="text-base md:text-lg font-semibold tracking-wide uppercase text-muted-foreground mb-4">
              Portfolio
            </h2>
            <h3 className="text-3xl md:text-4xl font-semibold mb-4">
              Featured Projects
            </h3>
            <p className="text-base text-muted-foreground max-w-2xl mx-auto">
              Explore our curated collection of residential and commercial spaces that showcase our design philosophy.
            </p>
          </div>
          
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 sm:gap-4 lg:gap-6 mb-8">
            {featuredImages.map((image, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, scale: 0.9 }}
                whileInView={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.4, delay: index * 0.05 }}
                viewport={{ once: true }}
              >
                <Card className="overflow-hidden group card-hover" data-testid={`featured-image-${index}`}>
                  <AspectRatio ratio={4/3}>
                    <img
                      src={image}
                      alt={`Interior design project ${index + 1}`}
                      className="w-full h-full object-cover"
                    />
                  </AspectRatio>
                </Card>
              </motion.div>
            ))}
          </div>
          
          <div className="text-center">
            <Link to="/gallery">
              <Button variant="secondary" size="lg" className="transition-colors duration-200" data-testid="view-full-gallery-button">
                View Full Gallery
                <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Before/After */}
      <section className="py-16 md:py-24">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-screen-xl">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-base md:text-lg font-semibold tracking-wide uppercase text-muted-foreground mb-4">
                Transformation
              </h2>
              <h3 className="text-3xl md:text-4xl font-semibold mb-6">
                See the Difference
              </h3>
              <p className="text-base leading-relaxed text-muted-foreground mb-6">
                Every space has potential. Our design approach transforms ordinary rooms into extraordinary environments. Slide to compare the before and after of this stunning residential project.
              </p>
              <ul className="space-y-3 text-sm text-muted-foreground">
                <li className="flex items-start">
                  <span className="text-primary mr-2">✓</span>
                  Space optimization and layout redesign
                </li>
                <li className="flex items-start">
                  <span className="text-primary mr-2">✓</span>
                  Custom furniture and lighting solutions
                </li>
                <li className="flex items-start">
                  <span className="text-primary mr-2">✓</span>
                  Premium materials and finishes
                </li>
              </ul>
            </div>
            
            <BeforeAfter
              beforeSrc="https://images.unsplash.com/photo-1646592474103-cfd22d1d9e34"
              afterSrc="https://images.pexels.com/photos/35579862/pexels-photo-35579862.jpeg"
            />
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-16 md:py-24 bg-muted">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-screen-xl">
          <div className="text-center mb-12">
            <h2 className="text-base md:text-lg font-semibold tracking-wide uppercase text-muted-foreground mb-4">
              Client Stories
            </h2>
            <h3 className="text-3xl md:text-4xl font-semibold">
              What Our Clients Say
            </h3>
          </div>
          
          <Carousel className="w-full max-w-4xl mx-auto" data-testid="testimonials-carousel">
            <CarouselContent>
              {testimonials.map((testimonial, index) => (
                <CarouselItem key={index} className="md:basis-1/2">
                  <Card className="p-8 h-full">
                    <p className="text-base leading-relaxed mb-6 italic">
                      "{testimonial.quote}"
                    </p>
                    <div>
                      <p className="font-semibold">{testimonial.author}</p>
                      <p className="text-sm text-muted-foreground">{testimonial.location}</p>
                    </div>
                  </Card>
                </CarouselItem>
              ))}
            </CarouselContent>
            <CarouselPrevious />
            <CarouselNext />
          </Carousel>
        </div>
      </section>

      {/* CTA Band */}
      <section className="py-16 md:py-24">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-screen-xl">
          <Card className="p-8 md:p-12 text-center bg-accent">
            <h3 className="text-3xl md:text-4xl font-semibold mb-4">
              Ready to Transform Your Space?
            </h3>
            <p className="text-base text-muted-foreground mb-8 max-w-2xl mx-auto">
              Let's discuss your project and create something beautiful together. Get in touch for a free consultation.
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <Link to="/contact">
                <Button size="lg" className="transition-colors duration-200" data-testid="cta-contact-button">
                  Schedule Consultation
                </Button>
              </Link>
              <a href="https://wa.me/918310951921" target="_blank" rel="noreferrer">
                <Button variant="secondary" size="lg" className="transition-colors duration-200" data-testid="cta-whatsapp-button">
                  Chat on WhatsApp
                </Button>
              </a>
            </div>
          </Card>
        </div>
      </section>
    </div>
  );
}